import './PositionHeader.scss';
import React from 'react';
import {
    MdBusinessCenter,
    MdChevronLeft,
    MdChevronRight,
    MdClose,
    MdEmojiEvents,
    MdFullscreen,
    MdHelpOutline,
    MdPayments,
    MdShoppingCart,
} from 'react-icons/md';
import Tooltip from '@srm/ui/tooltip';
import { IPosition } from '@models/winnerSelection/ITablePositions';
import { formatPrice } from '@utils/helpers';
import { PosInfo, getStatus, resolveWinnerNames } from '../utils/posInfoMerge';
import { EllipsisName } from './EllipsisName';
import { PositionDescription } from './PositionDescription';
import { PositionStatusPill } from './PositionStatusPill';
import { WinnerNamesChip } from './WinnerNamesChip';

interface IProps {
    pos: IPosition;
    /** Индекс текущей позиции в массиве (1-based padding для отображения «01/30»). */
    indexOneBased: number;
    /** Общее количество позиций. */
    total: number;
    /** Кэш offers-info для статуса/имени победителя. */
    posInfo: PosInfo | undefined;
    /** Fullscreen-режим (для иконки кнопки). */
    isFullscreen: boolean;
    onPrev: () => void;
    onNext: () => void;
    onToggleFullscreen: () => void;
}

/**
 * Шапка текущей позиции в main pane:
 * - имя с ellipsis (через <EllipsisName>) + status-pill;
 * - prev/next counter «01/30» + fullscreen-кнопка;
 * - meta-чипы (филиал, бюджет, кол-во, предложений) + winner-chip.
 *
 * indexOneBased и total — позиционирование уже посчитанное, не делаем findIndex
 * внутри (это дороже на больших списках).
 */
export const PositionHeader: React.FC<IProps> = ({
    pos,
    indexOneBased,
    total,
    posInfo: info,
    isFullscreen,
    onPrev,
    onNext,
    onToggleFullscreen,
}) => {
    const st = getStatus(pos, info);
    const ord = String(indexOneBased).padStart(2, '0');
    const totalStr = String(total).padStart(2, '0');
    const winnerNames = pos.winnerNames && pos.winnerNames.length > 0 ? pos.winnerNames : resolveWinnerNames(info);

    return (
        <div className="ws-pos-head">
            <div className="ws-pos-head__top">
                <h2 className="ws-pos-head__name">
                    <EllipsisName text={pos.name} />
                    <PositionStatusPill status={st} />
                    {/* Подсказка-бабл: показываем на всех статусах, кроме «Победитель
                        назначен» (decided) — там пояснять уже нечего. Открывается
                        наведением на ⓘ, оверлей не сдвигает шапку. */}
                    {st !== 'decided' ? (
                        <Tooltip
                            openDelay={120}
                            background="white"
                            position="bottom-start"
                            maxWidth="330px"
                            label={
                                <div className="ws-help">
                                    <div className="ws-help__hd">
                                        <MdEmojiEvents size="17px" aria-hidden />
                                        Как выбрать победителя
                                    </div>
                                    <p className="ws-help__lead">
                                        <b>«Основное»</b> у предложения — это <b>тип</b> (основное / альтернативное), а
                                        не выбранный победитель.
                                    </p>
                                    <p className="ws-help__how">
                                        Откройте меню предложения → <b>«Оценить»</b> и назначьте статус: основной,
                                        резервный или одобренный после теста.
                                    </p>
                                </div>
                            }
                        >
                            <span className="ws-pos-head__help" aria-label="Как выбрать победителя">
                                <MdHelpOutline size="17px" />
                            </span>
                        </Tooltip>
                    ) : null}
                </h2>
                <div className="ws-pos-head__actions">
                    <span className="ws-pos-head__counter" aria-label="Позиция">
                        <button
                            type="button"
                            onClick={onPrev}
                            disabled={total < 2}
                            aria-label="Предыдущая позиция"
                            title="Предыдущая (K, ↑)"
                        >
                            <MdChevronLeft size="14px" />
                        </button>
                        <span className="ws-pos-head__counter-text">
                            <b>{ord}</b>
                            <span className="sep">/</span>
                            <b>{totalStr}</b>
                        </span>
                        <button
                            type="button"
                            onClick={onNext}
                            disabled={total < 2}
                            aria-label="Следующая позиция"
                            title="Следующая (J, ↓)"
                        >
                            <MdChevronRight size="14px" />
                        </button>
                    </span>
                    <button
                        type="button"
                        className="ws-btn ws-btn--icon"
                        onClick={onToggleFullscreen}
                        title={isFullscreen ? 'Свернуть (Esc)' : 'Развернуть на весь экран (F)'}
                        aria-label={isFullscreen ? 'Свернуть' : 'На весь экран'}
                    >
                        {isFullscreen ? <MdClose size="18px" /> : <MdFullscreen size="18px" />}
                    </button>
                </div>
            </div>
            <div className="ws-pos-head__meta">
                {(pos.branch || pos.subdivision) && (
                    <span className="ws-pos-head__meta-chip">
                        <MdBusinessCenter size="14px" />
                        {pos.branch}
                        {pos.subdivision && (
                            <>
                                {pos.branch ? ' · ' : ''}
                                <span style={{ color: 'var(--neutral-60)' }}>{pos.subdivision}</span>
                            </>
                        )}
                    </span>
                )}
                {pos.price != null && (
                    <span className="ws-pos-head__meta-chip">
                        <MdPayments size="14px" />
                        Бюджет <b>{formatPrice(pos.price, pos.currencyCode)}</b>
                    </span>
                )}
                {pos.quantity != null && (
                    <span className="ws-pos-head__meta-chip">
                        <MdShoppingCart size="14px" />
                        Кол-во{' '}
                        <b>
                            {pos.quantity} {pos.units ?? ''}
                        </b>
                    </span>
                )}
                <WinnerNamesChip winnerNames={winnerNames} />
                <PositionDescription text={pos.description} variant="line" />
            </div>
        </div>
    );
};
